import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import { PlayArrow, EmojiEvents, Add, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { drawService } from '../services/drawService';
import { Draw } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { AppHeader } from '../components/AppHeader';
import { DrawsParticipant } from './participant/DrawsParticipant';

const Draws: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [draws, setDraws] = useState<Draw[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Fluxo do participante (usuário final) tem um visual dedicado, com busca
  // própria de dados — ver frontend/src/pages/participant/DrawsParticipant.tsx.
  const isParticipant = user?.role === 'USUARIO_FINAL';

  useEffect(() => {
    if (!isParticipant) {
      fetchDraws();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParticipant]);

  if (isParticipant) {
    return <DrawsParticipant />;
  }

  const fetchDraws = async () => {
    try {
      const data = await drawService.getAllDraws();
      console.log('🎲 Sorteios carregados:', data);
      setDraws(data);
    } catch (error) {
      console.error('❌ Erro ao carregar sorteios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (draw: Draw) => {
    const label = draw.prize?.name || draw.id;
    if (!window.confirm(`Excluir o sorteio "${label}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    try {
      setDeletingId(draw.id);
      await drawService.deleteDraw(draw.id);
      setDraws((prev) => prev.filter((d) => d.id !== draw.id));
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        'Não foi possível excluir o sorteio. Cancele-o antes se já tiver ganhadores.';
      alert(Array.isArray(message) ? message.join(', ') : message);
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pendente';
      case 'COMPLETED':
        return 'Concluído';
      case 'IN_PROGRESS':
        return 'Em Progresso';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'COMPLETED':
        return 'success';
      case 'IN_PROGRESS':
        return 'info';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <>
      <AppHeader 
        title="Sorteios" 
        subtitle="Gerencie e acompanhe todos os sorteios"
        icon={<EmojiEvents />}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Lista de Sorteios
          </Typography>
          {user?.role === 'ASSOCIACAO' && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<PlayArrow />}
                onClick={() => navigate('/draws/live')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Sortear ao Vivo
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/draws/create')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Novo Sorteio
              </Button>
            </Box>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Carregando sorteios...
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.light' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Campanha</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Prêmio</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Data do Sorteio</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {draws.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Nenhum sorteio encontrado
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Comece criando seu primeiro sorteio para ver a lista aqui.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => navigate('/draws/create')}
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            fontWeight: 600,
                            textTransform: 'none'
                          }}
                        >
                          Criar Primeiro Sorteio
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  draws.map((draw) => (
                    <TableRow key={draw.id}>
                      <TableCell>{draw.campaign?.name}</TableCell>
                      <TableCell>{draw.prize?.name}</TableCell>
                      <TableCell>
                        {new Date(draw.drawDate).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(draw.status)}
                          color={getStatusColor(draw.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {user?.role === 'ASSOCIACAO' && (
                            <>
                              {draw.status === 'PENDING' && (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  onClick={() => navigate(`/draws/${draw.id}/live`)}
                                  startIcon={<PlayArrow />}
                                  sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    textTransform: 'none'
                                  }}
                                >
                                  Iniciar Sorteio
                                </Button>
                              )}
                              {draw.status === 'IN_PROGRESS' && (
                                <>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => navigate(`/draws/${draw.id}/live`)}
                                    startIcon={<PlayArrow />}
                                    sx={{
                                      borderRadius: 2,
                                      fontWeight: 600,
                                      textTransform: 'none'
                                    }}
                                  >
                                    Continuar Sorteio
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    onClick={() => navigate(`/draws/${draw.id}/winners`)}
                                    startIcon={<EmojiEvents />}
                                    sx={{
                                      borderRadius: 2,
                                      fontWeight: 600,
                                      textTransform: 'none'
                                    }}
                                  >
                                    Ver Ganhadores
                                  </Button>
                                </>
                              )}
                              {draw.status === 'COMPLETED' && (
                                <Button
                                  variant="outlined"
                                  color="success"
                                  size="small"
                                  onClick={() => navigate(`/draws/${draw.id}/winners`)}
                                  startIcon={<EmojiEvents />}
                                  sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    textTransform: 'none'
                                  }}
                                >
                                  Ver Ganhadores
                                </Button>
                              )}
                              {(draw.status === 'PENDING' || draw.status === 'IN_PROGRESS') && (
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  onClick={() => navigate(`/draws/${draw.id}/edit`)}
                                  sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    textTransform: 'none'
                                  }}
                                >
                                  Editar
                                </Button>
                              )}
                              {(draw.status === 'CANCELLED' || draw.status === 'PENDING') && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<Delete />}
                                  disabled={deletingId === draw.id}
                                  onClick={() => handleDelete(draw)}
                                  sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    textTransform: 'none'
                                  }}
                                >
                                  Excluir
                                </Button>
                              )}
                            </>
                          )}
                          
                          {/* Botão para USUARIO_FINAL ver ganhadores aprovados */}
                          {user?.role === 'USUARIO_FINAL' && (draw.status === 'IN_PROGRESS' || draw.status === 'COMPLETED') && (
                            <Button
                              variant="outlined"
                              color="success"
                              size="small"
                              onClick={() => navigate(`/draws/${draw.id}/winners`)}
                              startIcon={<EmojiEvents />}
                              sx={{
                                borderRadius: 2,
                                fontWeight: 600,
                                textTransform: 'none'
                              }}
                            >
                              Ver Ganhadores
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </>
  );
};
export default Draws;